import FireResistantGlass from './Glass/FireResistantGlass';
import GenericGlass from './Glass/GenericGlass';
import GlassDBView from './GlassDBView';

export default class GlassDB {
	static GENERIC_EI30_ID = 101;
	static GENERIC_EI60_ID = 121;

	// Will be populated dynamically from CSV, but starts with virtual glasses
	static PricedFRGGlasses = {
		GENERIC_EI30: GlassDB.GENERIC_EI30_ID,
		GENERIC_EI60: GlassDB.GENERIC_EI60_ID,
	};

	constructor() {
		this.loaded = false; // toggled by GlassDbLoader
		// Generic FRG price mapping: { genericId: [specificId1, specificId2, ...] }
		// Loaded from configuratorData.generic_frgs (WordPress)
		this.genericPriceFRGs = {};
		// Dynamic FRG data parsed from CSV
		this.priceCSVFrgNames = null; // FRG names in order from FRG_STR row
		this.priceCSVFrgIds = null; // FRG IDs in order from FRG_ID row
		this.db = {
			frg: [],
			mid: [],
			out: [],
			uValue: { '1-glazed': [] },
			glassTranslations: {},
			glassPrices: {
				0: { '1-glazed': {}, '2-glazed': {}, '3-glazed': {} },
			},
			datasheets: {
				frg: [],
				mid: [],
				out: [],
				loaded: false,
			},
		};
	}

	addRow(glassLabel, data, ctx) {
		if (ctx && glassLabel.startsWith('prices_')) {
			return this.addPriceRow(data, ctx);
		}
		const glassType = glassLabel === 'frg' ? FireResistantGlass : GenericGlass;
		const aGlass = new glassType(data);
		if (
			glassType === FireResistantGlass &&
			ctx?.enabledManufacturers &&
			ctx.enabledManufacturers.findIndex(m => m === aGlass.manufacturer) === -1
		) {
			return;
		}
		aGlass.dbIndex = this.db[glassLabel].length;
		this.db[glassLabel].push(aGlass);

		if (glassType === FireResistantGlass && aGlass.u_value) {
			this.db['uValue']['1-glazed'].push(aGlass);
		}
	}

	addTranslation(translation) {
		const intlDb = {};
		this.db['glassTranslations'] = intlDb;
		translation.forEach(o => {
			intlDb[o.id] = o;
		});
	}

	addVerasIds(verasIds) {
		this.db['verasIds'] = structuredClone(verasIds);
	}

	addPriceRow(priceRow, ctx) {
		if (!priceRow[0]) {
			return; // skip entries with nothing in first column
		}
		const rowType = priceRow[0];

		// Parse FRG_STR row: extract FRG names at offsets 3, 6, 9, ...
		if (rowType === 'FRG_STR') {
			this.priceCSVFrgNames = [];
			let col = 3;
			while (col < priceRow.length) {
				const name = priceRow[col]?.trim();
				if (!name) {
					break;
				}
				this.priceCSVFrgNames.push(name);
				col += 3;
			}
			return;
		}

		// Parse FRG_ID row: extract FRG IDs and update PricedFRGGlasses mapping
		if (rowType === 'FRG_ID') {
			if (this.priceCSVFrgNames === null) {
				console.warn('Prices CSV is missing FRG_STR');
				this.priceCSVFrgNames = [];
				return;
			}
			this.priceCSVFrgIds = [];
			let col = 3;
			let nameIdx = 0;
			while (col < priceRow.length && nameIdx < this.priceCSVFrgNames.length) {
				const id = parseInt(priceRow[col]);
				if (!isNaN(id)) {
					this.priceCSVFrgIds.push(id);
					// Update PricedFRGGlasses with name→ID mapping
					// Convert name to key format: "FSF 30-15" → "FSF30_15"
					// const keyName = this.priceCSVFrgNames[nameIdx].replace(/[\s-]/g, '_').replace(/_+/g, '_');
					GlassDB.PricedFRGGlasses[this.priceCSVFrgNames[nameIdx]] = id;
				}
				col += 3;
				nameIdx += 1;
			}
			return;
		}

		const vol = ctx.volume;
		if (this.db.glassPrices[vol] === undefined) {
			this.db.glassPrices[vol] = { '1-glazed': {}, '2-glazed': {}, '3-glazed': {} };
		}
		// MONO block: single-glazed FRG prices
		if (rowType === 'MONO') {
			if (this.priceCSVFrgIds === null) {
				console.warn('Prices CSV is missing FRG_ID');
				this.priceCSVFrgIds = [];
				return;
			}
			const price = {};

			// Parse prices for each dynamic FRG
			this.priceCSVFrgIds.forEach((frgId, idx) => {
				const col = 3 + idx * 3; // offset 3 for first FRG, then +3 for each
				const priceVal = parseInt(priceRow[col]);
				price[frgId] = priceVal;
			});

			// Calculate generic prices from genericPriceFRGs mapping
			Object.entries(this.genericPriceFRGs).forEach(([genericIdStr, specificIds]) => {
				const genericId = parseInt(genericIdStr);
				const prices = specificIds.map(id => price[id]).filter(p => p !== undefined && !isNaN(p));
				if (prices.length > 0) {
					price[genericId] = Math.max(...prices);
				}
			});

			this.db['glassPrices'][vol]['1-glazed'] = price;
			return;
		}

		// ISO2/ISO3 rows: outer glass with FRG + gas combinations
		const outGlassId = parseInt(priceRow[1]);
		if (isNaN(outGlassId)) {
			return;
		}

		const numFRGs = this.priceCSVFrgIds.length;
		const ISO2_BLOCK_WIDTH = numFRGs * 3; // each FRG has 3 gas columns
		const ISO3_BLOCK_WIDTH = 1 + numFRGs * 3; // mid glass column + FRG*gas columns

		// Calculate struct offsets dynamically
		const OFFSET_2GLAZED = 3;
		const OFFSET_3GLAZED_FIRST = OFFSET_2GLAZED + ISO2_BLOCK_WIDTH;

		// Build struct_mid_glass_offsets by scanning for ISO3 blocks
		// ISO3 blocks have the mid glass IDs in their first column
		const struct_mid_glass_offsets = [OFFSET_2GLAZED];
		let nextOffset = OFFSET_3GLAZED_FIRST;
		while (nextOffset < priceRow.length && priceRow[nextOffset]) {
			struct_mid_glass_offsets.push(nextOffset);
			nextOffset += ISO3_BLOCK_WIDTH;
		}

		// Build frg_glass_offsets dynamically from frgIds
		const frg_glass_offsets = {};
		this.priceCSVFrgIds.forEach((frgId, idx) => {
			frg_glass_offsets[idx * 3] = frgId;
		});

		// Build generic trigger map: keyed by first specific ID's offset
		// When we encounter that FRG offset, we compute max of all specific IDs
		const genericTriggers = {};
		Object.entries(this.genericPriceFRGs).forEach(([genericIdStr, specificIds]) => {
			if (specificIds.length === 0) return;
			const firstIdx = this.priceCSVFrgIds.indexOf(specificIds[0]);
			if (firstIdx >= 0) {
				genericTriggers[firstIdx * 3] = {
					genericId: parseInt(genericIdStr),
					specificOffsets: specificIds.map(id => {
						const idx = this.priceCSVFrgIds.indexOf(id);
						return idx >= 0 ? idx * 3 : -1;
					}),
				};
			}
		});

		const OFFSET_ARGON = 0;
		const OFFSET_MIXED = 1;
		const OFFSET_KRYPTON = 2;
		const gas_offsets = {
			[OFFSET_ARGON]: 'argon',
			[OFFSET_MIXED]: 'mixedGas',
			[OFFSET_KRYPTON]: 'krypton',
		};

		struct_mid_glass_offsets.forEach(s => {
			Object.keys(frg_glass_offsets).forEach(fKey => {
				const f = parseInt(fKey);
				Object.keys(gas_offsets).forEach(gKey => {
					const g = parseInt(gKey);
					if (s === OFFSET_2GLAZED) {
						const iso2 = this.db['glassPrices'][vol]['2-glazed'];
						if (iso2[outGlassId] === undefined) {
							iso2[outGlassId] = [];
						}
						const outGlass = iso2[outGlassId];
						const price = parseInt(priceRow[s + f + g]);
						outGlass.push({ gas: gas_offsets[g], frgId: frg_glass_offsets[f], price });

						if (genericTriggers[f]) {
							const { genericId, specificOffsets } = genericTriggers[f];
							const prices = specificOffsets
								.map(offset => (offset >= 0 ? parseInt(priceRow[s + offset + g]) : 0))
								.filter(p => !isNaN(p));
							if (prices.length > 0) {
								outGlass.push({
									gas: gas_offsets[g],
									frgId: genericId,
									price: Math.max(...prices),
								});
							}
						}
					} else {
						const iso3 = this.db['glassPrices'][vol]['3-glazed'];
						if (iso3[outGlassId] === undefined) {
							iso3[outGlassId] = [];
						}
						const glassIdsStr = priceRow[s];
						if (!glassIdsStr) return;
						const glassIds = glassIdsStr.split(',');
						const outGlass = iso3[outGlassId];
						const price = parseInt(priceRow[s + 1 + f + g]);
						glassIds.forEach(mgidStr => {
							const mgid = parseInt(mgidStr);
							outGlass.push({
								midGlassId: mgid,
								gas: gas_offsets[g],
								frgId: frg_glass_offsets[f],
								price,
							});

							if (genericTriggers[f]) {
								const { genericId, specificOffsets } = genericTriggers[f];
								const prices = specificOffsets
									.map(offset => (offset >= 0 ? parseInt(priceRow[s + 1 + offset + g]) : 0))
									.filter(p => !isNaN(p));
								if (prices.length > 0) {
									outGlass.push({
										midGlassId: mgid,
										gas: gas_offsets[g],
										frgId: genericId,
										price: Math.max(...prices),
									});
								}
							}
						});
					}
				});
			});
		});
	}

	translateGlass(glass, lang) {
		let intlDb = this.db['glassTranslations'];
		let id = parseInt(glass.id);
		return (intlDb[id] && intlDb[id][lang]) || glass.glasstype;
	}

	getGlassById(glassId, glassLabel) {
		return this.db[glassLabel].find(g => g.id === glassId);
	}

	getVerasIds(flatGlassStructure) {
		const frgGlassVerasId = this.getGlassById(
			flatGlassStructure.fireResistantGlassId,
			'frg'
		)?.id_veras;
		const frgGlassVerasType = 'EFG';
		const middleGlassVerasId =
			(flatGlassStructure.middleGlassId &&
				this.getGlassById(flatGlassStructure.middleGlassId, 'mid')?.id_veras) ||
			null;
		const middleGlassVerasType = 'EFG';
		const outerGlassVerasId =
			(flatGlassStructure.outerGlassId &&
				this.getGlassById(flatGlassStructure.outerGlassId, 'out')?.id_veras) ||
			null;
		const outerGlassVerasType = flatGlassStructure.outerGlassId >= 225 ? 'VSG' : 'EFG'; // TODO: Use separate VERAS_TYPE field in CSV instead of hardcoding
		const verasDb = this.db.verasIds;
		const productType = flatGlassStructure.structure === '1-glazed' ? 'EFG' : 'ISO';
		const productId =
			(flatGlassStructure.structure === '1-glazed'
				? frgGlassVerasId
				: flatGlassStructure.structure === '2-glazed'
					? verasDb.structures.find(s => s.mono_id === frgGlassVerasId && s.iso === 2)?.iso_id
					: verasDb.structures.find(s => s.mono_id === frgGlassVerasId && s.iso === 3)?.iso_id) ||
			null;
		const spacerId =
			verasDb.spacers.find(g => g.spacer === flatGlassStructure.spacerOutside)?.id || null;
		const gasId = verasDb.gases.find(g => g.gas === flatGlassStructure.gasOutside)?.id || null;

		return {
			productId,
			productType,
			frgGlassVerasId,
			frgGlassVerasType,
			middleGlassVerasId,
			middleGlassVerasType,
			outerGlassVerasId,
			outerGlassVerasType,
			spacerId,
			gasId,
		};
	}

	getGlassPrices(glassStruct) {
		return Object.fromEntries(
			Object.entries(this.db.glassPrices).map(([vol, _]) => [
				vol,
				this.getVolumeDiscountedGlassPrice(vol, glassStruct),
			])
		);
	}

	getPriceVolumeCat(m2) {
		// Iterate over all prices since they might not be sorted if not all prices are ints
		return Object.keys(this.db.glassPrices).reduce((best, k) => {
			const n = parseFloat(k);
			if (isNaN(n)) return best; // skip non-numeric keys
			if (best === null || (best === null && !m2)) return k; // !m2: return first result if m2 is unset (the first should be '0')
			const d = m2 - n;
			return d >= 0 && d < m2 - parseFloat(best) ? k : best;
		}, null);
	}

	getVolumeDiscountedGlassPrice(priceVolumeCat, glassStruct) {
		const frgId = parseInt(glassStruct.fireResistantGlass.id);
		if (Object.values(GlassDB.PricedFRGGlasses).indexOf(frgId) < 0) {
			return null;
		}

		const nullret = ret => (ret === undefined ? null : ret);
		const prices = this.db['glassPrices'][priceVolumeCat];
		const struct = glassStruct.getGlassStructure();
		if (struct === '1-glazed') {
			return nullret(prices['1-glazed'][frgId]);
		}
		let outGlassId = parseInt(glassStruct.outerGlass.id);
		if (struct === '2-glazed') {
			return nullret(
				prices['2-glazed'][parseInt(outGlassId)]?.find(
					g => g.frgId === frgId && g.gas === glassStruct.gasOutside
				)?.price
			);
		}
		return nullret(
			prices['3-glazed'][outGlassId]?.find(
				g =>
					g.frgId === frgId &&
					g.gas === glassStruct.gasOutside &&
					g.midGlassId === parseInt(glassStruct.middleGlass.id)
			)?.price
		);
	}

	getDatasheet(flatGlassStructure, preferredLanguage = null) {
		if (flatGlassStructure.structure !== '1-glazed') return; // only return DS on MONO structures
		const frgId = flatGlassStructure.fireResistantGlassId;
		return this.getGlassById(frgId, 'frg')?.getDatasheet(preferredLanguage);
	}

	createView() {
		return new GlassDBView(this);
	}
}
